import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Flex, Icon, Text, Input, icon as iconSize, useKeyboardSelectableList } from '@nula/core-ui';
import { FloatingPanel } from '@/components/floating-panel/floating-panel';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { WorkspaceServiceContext } from '@/lib/workspace/use-workspace-service';
import { filterCanvases } from './canvas-picker-filter';
import { deferToAfterCommit } from './defer-after-commit';
import styles from './editor-canvas-picker.module.css';

export interface EditorCanvasPickerProps {
  editor: Editor;
}

const NEW_CANVAS_KEY = '__new_canvas__';

type PickerRow = { id: string; title: string; isNew?: boolean };

/**
 * Canvas picker for the `/canvas` embed command — a caret-anchored popover with
 * search (mirrors {@link EditorDocPicker}, NOT a modal). Opened when the slash
 * command dispatches `nula-show-canvas-picker`. Picking an existing canvas
 * inserts the embed via `insertCanvasEmbed`; the pinned "New canvas" row creates
 * a fresh canvas entity first, then inserts.
 */
export function EditorCanvasPicker({ editor }: EditorCanvasPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const docs = useWorkspaceDocs();
  // Read the context directly (not the throwing useWorkspaceService) so the
  // always-mounted picker doesn't require a provider in every editor host /
  // test — the service is only needed when "New canvas" is chosen.
  const service = useContext(WorkspaceServiceContext);

  useEffect(() => {
    const show = () => {
      const { from } = editor.state.selection;
      const coords = editor.view.coordsAtPos(from);
      setPos({ top: coords.bottom + 8, left: coords.left });
      setQuery('');
      setOpen(true);
    };
    window.addEventListener('nula-show-canvas-picker', show);
    return () => window.removeEventListener('nula-show-canvas-picker', show);
  }, [editor]);

  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const rows: PickerRow[] = useMemo(() => {
    const canvases = filterCanvases(docs, query).map((c) => ({
      id: c.id,
      title: c.title || 'Untitled',
    }));
    return [...canvases, { id: NEW_CANVAS_KEY, title: 'New canvas', isNew: true }];
  }, [docs, query]);

  // Defer the insert past the React commit that unmounts the picker/slash-menu
  // (see deferToAfterCommit) — a synchronous insert wedges the y-prosemirror
  // binding so the embed never reaches the Y.Doc. Both paths defer: the "new"
  // path (await createDoc) was the reliable repro; the existing path uses the
  // same deferral to avoid the same latent hazard.
  const insertCanvas = (canvasId: string) => {
    deferToAfterCommit(() => {
      if (editor.isDestroyed) return;
      editor.chain().focus().insertCanvasEmbed(canvasId).run();
    });
  };

  const { highlightedIndex, getItemProps, getListProps } = useKeyboardSelectableList({
    items: rows,
    onSelect: async (row) => {
      setOpen(false);
      if (row.isNew) {
        if (!service) return;
        // Create the canvas directly (not via useCreateEntity, which navigates
        // away) so we stay in the document and embed the fresh canvas inline.
        const { id } = await service.createDoc({ kind: 'canvas' });
        insertCanvas(id);
      } else {
        insertCanvas(row.id);
      }
    },
  });

  if (!open) return null;

  return (
    <FloatingPanel ref={panelRef} top={pos.top} left={pos.left} minWidth={280}>
      <Flex vertical gap="xs" className={styles.body}>
        <Input
          autoFocus
          placeholder="Search canvases…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          {...getListProps()}
        />
        {/* Existing canvases scroll; the pinned "New canvas" row (always last in
            `rows`) renders outside the scroll area so it stays visible no matter
            how many canvases exist. Indices stay aligned with `rows` so keyboard
            nav still reaches it. */}
        <Flex vertical className={styles.list}>
          {rows.map((row, i) =>
            row.isNew ? null : (
              <button
                key={row.id}
                type="button"
                className={styles.item}
                data-active={i === highlightedIndex}
                {...getItemProps(i)}
              >
                <Icon name="gesture" size={iconSize.sm} className={styles.itemIcon} />
                <Text className={styles.itemLabel}>{row.title}</Text>
              </button>
            ),
          )}
        </Flex>
        {rows.map((row, i) =>
          row.isNew ? (
            <button
              key={row.id}
              type="button"
              className={styles.newItem}
              data-active={i === highlightedIndex}
              {...getItemProps(i)}
            >
              <Icon name="add" size={iconSize.sm} className={styles.itemIcon} />
              <Text className={styles.itemLabel}>{row.title}</Text>
            </button>
          ) : null,
        )}
      </Flex>
    </FloatingPanel>
  );
}
